import React, { useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import { ChevronUp, Plus } from 'lucide-react';

import DigitalImpactChart from './DigitalImpactChart';
import ImprovementDistributionChart from './ImprovementDistributionChart';
import InvestmentGrowthChart from './InvestmentGrowthChart';
import MultidimensionalImpactChart from './MultidimensionalImpactChart';
import ROIServiceChart from './ROIServiceChart';
import FadeInSection from './ui/FadeInSection';

interface ExpandableChartsProps {
  className?: string;
}

const ExpandableCharts: React.FC<ExpandableChartsProps> = ({ className = '' }) => {
  // Estados para controlar la expansión de los gráficos
  const [initialExpanded, setInitialExpanded] = useState<boolean>(false);
  const [fullyExpanded, setFullyExpanded] = useState<boolean>(false);

  // Función para expandir la primera fase de gráficos
  const handleInitialExpand = () => {
    setInitialExpanded(true);
    // Scroll suave hacia el primer gráfico
    setTimeout(() => {
      document
        .getElementById('initial-charts')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Función para expandir todos los gráficos
  const handleFullExpand = () => {
    setFullyExpanded(true);
    // Scroll suave hacia el primer gráfico adicional
    setTimeout(() => {
      document
        .getElementById('additional-charts')
        ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  // Función para colapsar todos los gráficos
  const handleCollapseAll = () => {
    setFullyExpanded(false);
    setInitialExpanded(false);
    // Scroll hacia arriba
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`${className}`} data-testid="expandable-charts-container">
      <h2 className="text-3xl font-bold text-gray-900 mb-4 text-center">Resultados Medibles</h2>
      {/* Botón para expandir los gráficos iniciales */}
      {!initialExpanded && (
        <FadeInSection delay={0.5}>
          <div className="flex justify-center my-8">
            <motion.button
              onClick={handleInitialExpand}
              className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="h-5 w-5" />
              <span className="font-medium">Ver gráficos de resultados</span>
            </motion.button>
          </div>
        </FadeInSection>
      )}

      {/* Gráficos iniciales (ROI y Crecimiento de Inversión) */}
      <AnimatePresence>
        {initialExpanded && (
          <motion.div
            id="initial-charts"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FadeInSection delay={0.1}>
              <div className="py-20 bg-white rounded-xl my-12 shadow-lg border border-gray-100">
                <ROIServiceChart className="" data-testid="roi-chart" />
              </div>
            </FadeInSection>

            <div className="my-16 border-b border-gray-200 max-w-4xl mx-auto opacity-30"></div>

            <FadeInSection delay={0.2}>
              <div className="py-20 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl my-12 shadow-lg border border-blue-100">
                <InvestmentGrowthChart className="" data-testid="investment-growth-chart" />
              </div>
            </FadeInSection>

            {/* Botón para expandir todos los gráficos o colapsar */}
            <div className="flex justify-center my-8">
              {!fullyExpanded ? (
                <motion.button
                  onClick={handleFullExpand}
                  className="flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-full shadow-lg hover:bg-blue-700 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <Plus className="h-5 w-5" />
                  <span className="font-medium">Ver más gráficos</span>
                </motion.button>
              ) : (
                <motion.button
                  onClick={handleCollapseAll}
                  className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  <ChevronUp className="h-5 w-5" />
                  <span className="font-medium">Mostrar menos</span>
                </motion.button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gráficos adicionales (Impacto Digital, Distribución de Mejoras, Impacto Multidimensional) */}
      <AnimatePresence>
        {initialExpanded && fullyExpanded && (
          <motion.div
            id="additional-charts"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.5 }}
          >
            <FadeInSection delay={0.1}>
              <div className="py-16 bg-white rounded-xl my-8 shadow-lg">
                <DigitalImpactChart className="" data-testid="digital-impact-chart" />
              </div>
            </FadeInSection>

            <FadeInSection delay={0.2}>
              <div className="py-16 bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl my-8 shadow-lg">
                <ImprovementDistributionChart
                  className=""
                  data-testid="improvement-distribution-chart"
                />
              </div>
            </FadeInSection>

            <FadeInSection delay={0.3}>
              <div className="py-16 bg-white rounded-xl my-8 shadow-lg">
                <MultidimensionalImpactChart
                  className=""
                  data-testid="multidimensional-impact-chart"
                />
              </div>
            </FadeInSection>

            {/* Botón para colapsar todos los gráficos */}
            <div className="flex justify-center my-8">
              <motion.button
                onClick={handleCollapseAll}
                className="flex items-center gap-2 px-6 py-3 bg-gray-600 text-white rounded-full shadow-lg hover:bg-gray-700 transition-all duration-300"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <ChevronUp className="h-5 w-5" />
                <span className="font-medium">Mostrar menos</span>
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ExpandableCharts;
